import type { StickyBannerContent } from '@sbt-web/header';
import { zeroUUID } from '@sbt-web/optimizely-sdk';
import { getCategoryForStickyBanner } from '@/client/components/Scaffolding/utils/getCategoryForStickyBanner';
import { getSSROptimizelyClient } from '@/tools/optimizely/ssrClient';

/*
 * Utility function to check if the returned Optimizely configuration is valid
 */
const isNonEmptyConfig = (config: unknown) =>
  !!config && typeof config === 'object' && Object.keys(config).length > 0;

/*
 * Utility function to generate an Optimizely configuration path based on placement and category
 */
const getFeatureVariablePath = (
  placement: string,
  categorySlug?: string
): string => {
  const CUSTOM_STICKY_BANNERS = ['auto', 'moto-e-scooter'];

  if (categorySlug && CUSTOM_STICKY_BANNERS.includes(categorySlug)) {
    return `subito-product-marketing.${placement}.${categorySlug}.sticky-banner`;
  }

  return `subito-product-marketing.${placement}.sticky-banner`;
};

export const getStickyBannerContent = async (
  section: 'adview' | 'listing',
  category?: string | string[]
): Promise<StickyBannerContent | undefined> => {
  const ssrOptimizely = await getSSROptimizelyClient();

  if (!ssrOptimizely) {
    return;
  }

  const categorySlug = getCategoryForStickyBanner(category);
  const featureVariablePath = getFeatureVariablePath(section, categorySlug);
  const isFeatureEnabled = ssrOptimizely.isFeatureEnabled(
    featureVariablePath,
    zeroUUID
  );

  if (!isFeatureEnabled) {
    return;
  }

  const config = ssrOptimizely.getAllFeatureVariables(
    featureVariablePath,
    zeroUUID
  ) as StickyBannerContent;

  if (!isNonEmptyConfig(config)) {
    return;
  }

  return config;
};
